import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/use-toast';
import { Search, Save, Users, CheckSquare } from 'lucide-react';
import { MomentumScore } from '@/api/entities';
import { PersonaActivity } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';

const ENTITIES = [
  { value: 'MomentumScore', label: 'Momentum Scores' },
  { value: 'PersonaActivity', label: 'Persona Activities' },
  { value: 'AccountFinal', label: 'Accounts' },
  { value: 'PersonFinal', label: 'People' },
];

const TENANTS = [
  { value: 'instructure', label: 'Instructure' },
  { value: 'clairelabs', label: 'Claire Labs' },
  { value: 'demarque', label: 'Demarque' },
  { value: 'demarque_V2', label: 'Demarque V2' },
  { value: 'smowl', label: 'Smowl' },
  { value: 'diambra', label: 'Diambra' },
  { value: 'edqwest', label: 'EdQwest' },
  { value: 'calmedica', label: 'Calmedica' },
];

const getEntityModule = (entityName) => {
  switch(entityName) {
    case 'MomentumScore': return MomentumScore;
    case 'PersonaActivity': return PersonaActivity;
    case 'AccountFinal': return AccountFinal;
    case 'PersonFinal': return PersonFinal;
    default: return null;
  }
};

export default function MultiTenantAccessManager() {
  const [selectedEntity, setSelectedEntity] = useState('MomentumScore');
  const [searchTerm, setSearchTerm] = useState('');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingRecords, setEditingRecords] = useState({});
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;
  const { toast } = useToast();

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      const EntityModule = getEntityModule(selectedEntity);
      if (!EntityModule) {
        throw new Error('Invalid entity selected');
      }

      let data;
      if (selectedEntity === 'MomentumScore') {
        data = await EntityModule.list('-date_published');
      } else if (selectedEntity === 'PersonaActivity') {
        data = await EntityModule.list('-date');
      } else if (selectedEntity === 'AccountFinal') {
        data = await EntityModule.list('-account_fit_score');
      } else if (selectedEntity === 'PersonFinal') {
        data = await EntityModule.list('-persona_fit_score');
      }
      
      setRecords(data);
      setCurrentPage(1);
      setSelectedRecords(new Set());
      setEditingRecords({});
      toast({ title: 'Records loaded', description: `Found ${data.length} records` });
    } catch (error) {
      console.error('Error loading records:', error);
      toast({ title: 'Error', description: 'Failed to load records', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTenantToggle = (recordId, tenantId) => {
    setEditingRecords(prev => {
      const record = records.find(r => r.id === recordId);
      const current = prev[recordId] || { visible_to_tenants: record?.visible_to_tenants || [] };
      const tenants = new Set(current.visible_to_tenants || []);
      
      if (tenants.has(tenantId)) {
        tenants.delete(tenantId);
      } else {
        tenants.add(tenantId);
      }
      
      return {
        ...prev,
        [recordId]: {
          ...current,
          visible_to_tenants: Array.from(tenants)
        }
      };
    });
  };

  const handleSelectRecord = (recordId) => {
    setSelectedRecords(prev => {
      const next = new Set(prev);
      if (next.has(recordId)) {
        next.delete(recordId);
      } else {
        next.add(recordId);
      }
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecords(new Set(paginatedRecords.map(r => r.id)));
    } else {
      setSelectedRecords(new Set());
    }
  };

  const handleSelectAllFiltered = () => {
    setSelectedRecords(new Set(filteredRecords.map(r => r.id)));
    toast({ 
      title: 'All records selected', 
      description: `Selected all ${filteredRecords.length} filtered records` 
    });
  };

  const handleApplyToSelected = (tenantId, add) => {
    const updates = {};
    selectedRecords.forEach(recordId => {
      const record = records.find(r => r.id === recordId);
      const current = editingRecords[recordId] || { visible_to_tenants: record?.visible_to_tenants || [] };
      const tenants = new Set(current.visible_to_tenants || []);
      
      if (add) {
        tenants.add(tenantId);
      } else {
        tenants.delete(tenantId);
      }
      
      updates[recordId] = {
        ...current,
        visible_to_tenants: Array.from(tenants)
      };
    });
    
    setEditingRecords(prev => ({ ...prev, ...updates }));
    toast({ 
      title: 'Applied to selected', 
      description: `${add ? 'Added' : 'Removed'} ${tenantId} ${add ? 'to' : 'from'} ${selectedRecords.size} records` 
    });
  };

  const handleSaveSingle = async (record) => {
    try {
      const updates = editingRecords[record.id];
      if (!updates) return;

      const EntityModule = getEntityModule(selectedEntity);
      await EntityModule.update(record.id, updates);

      setRecords(records.map(r => r.id === record.id ? { ...r, ...updates } : r));
      
      const newEditing = { ...editingRecords };
      delete newEditing[record.id];
      setEditingRecords(newEditing);

      toast({ title: 'Saved', description: 'Multi-tenant access updated' });
    } catch (error) {
      console.error('Error saving:', error);
      toast({ title: 'Error', description: 'Failed to save changes', variant: 'destructive' });
    }
  };

  const handleSaveAll = async () => {
    const recordsToSave = Object.keys(editingRecords);
    if (recordsToSave.length === 0) {
      toast({ title: 'No changes', description: 'No records have been modified', variant: 'default' });
      return;
    }

    setIsSaving(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      toast({ 
        title: 'Saving...', 
        description: `Updating ${recordsToSave.length} records. Please wait...`,
        duration: 10000
      });

      const EntityModule = getEntityModule(selectedEntity);

      for (const recordId of recordsToSave) {
        try {
          const updates = editingRecords[recordId];
          await EntityModule.update(recordId, updates);

          setRecords(prev => prev.map(r => r.id === recordId ? { ...r, ...updates } : r));
          successCount++;

          if (successCount < recordsToSave.length) {
            await new Promise(resolve => setTimeout(resolve, 700));
          }

          if (successCount % 10 === 0) {
            toast({ 
              title: 'Progress...', 
              description: `Saved ${successCount} of ${recordsToSave.length} records`,
              duration: 3000
            });
          }
        } catch (error) {
          console.error(`Error saving record ${recordId}:`, error);
          errorCount++;
          
          if (error.response?.status === 429 || error.message?.includes('429')) {
            console.warn('Rate limit hit, adding extra delay...');
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      setEditingRecords({});
      
      if (errorCount === 0) {
        toast({ 
          title: 'All saved!', 
          description: `Successfully updated ${successCount} records`,
          duration: 5000
        });
      } else {
        toast({ 
          title: 'Partially saved', 
          description: `${successCount} succeeded, ${errorCount} failed. Try saving the failed records again.`,
          variant: 'default',
          duration: 7000
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const filteredRecords = records.filter(record => {
    const searchLower = searchTerm.toLowerCase();
    if (selectedEntity === 'MomentumScore') {
      return record.institution_name?.toLowerCase().includes(searchLower) ||
             record.summary?.toLowerCase().includes(searchLower);
    } else if (selectedEntity === 'PersonaActivity') {
      return record.full_name?.toLowerCase().includes(searchLower) ||
             record.summary?.toLowerCase().includes(searchLower);
    } else if (selectedEntity === 'AccountFinal') {
      return record.account_name?.toLowerCase().includes(searchLower);
    } else if (selectedEntity === 'PersonFinal') {
      const fullName = `${record.first_name || ''} ${record.last_name || ''}`.toLowerCase();
      return fullName.includes(searchLower) ||
             record.account_name?.toLowerCase().includes(searchLower);
    }
    return false;
  });

  const getDisplayName = (record) => {
    if (selectedEntity === 'MomentumScore') {
      return `${record.institution_name} - ${record.summary?.substring(0, 50)}...`;
    } else if (selectedEntity === 'PersonaActivity') {
      return `${record.full_name || `${record.first_name} ${record.last_name}`} - ${record.summary?.substring(0, 50)}...`;
    } else if (selectedEntity === 'AccountFinal') {
      return record.account_name;
    } else if (selectedEntity === 'PersonFinal') {
      return `${record.first_name} ${record.last_name} @ ${record.account_name}`;
    }
    return '';
  };

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);
  
  const allPageRecordsSelected = paginatedRecords.length > 0 && paginatedRecords.every(r => selectedRecords.has(r.id));
  const allFilteredRecordsSelected = filteredRecords.length > 0 && filteredRecords.every(r => selectedRecords.has(r.id));
  const hasChanges = Object.keys(editingRecords).length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Multi-Tenant Access Manager
        </CardTitle>
        <CardDescription>
          Configure which tenants can see records across entities
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Entity</Label>
            <Select value={selectedEntity} onValueChange={setSelectedEntity}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITIES.map(entity => (
                  <SelectItem key={entity.value} value={entity.value}>
                    {entity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        <Button onClick={loadRecords} disabled={isLoading} className="w-full">
          {isLoading ? 'Loading...' : 'Load Records'}
        </Button>

        {selectedRecords.size > 0 && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-blue-900">
                {selectedRecords.size} record(s) selected
                {selectedRecords.size < filteredRecords.length && (
                  <span className="text-xs ml-2">
                    (of {filteredRecords.length} total)
                  </span>
                )}
              </p>
              <div className="flex gap-2">
                {!allFilteredRecordsSelected && selectedRecords.size > 0 && selectedRecords.size < filteredRecords.length && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleSelectAllFiltered}
                  >
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Select All {filteredRecords.length}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedRecords(new Set())}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Label className="text-xs text-blue-900">Apply to selected:</Label>
              {TENANTS.map(tenant => (
                <div key={tenant.value} className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleApplyToSelected(tenant.value, true)}
                    className="text-xs"
                  >
                    + {tenant.label}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleApplyToSelected(tenant.value, false)}
                    className="text-xs"
                  >
                    - {tenant.label}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasChanges && (
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveAll} 
              disabled={isSaving}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : `Save All Changes (${Object.keys(editingRecords).length})`}
            </Button>
          </div>
        )}

        {filteredRecords.length > 0 && (
          <>
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Showing {startIndex + 1}-{Math.min(endIndex, filteredRecords.length)} of {filteredRecords.length} records</span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="flex items-center px-2">Page {currentPage} of {totalPages}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-center">
                      <Checkbox
                        checked={allPageRecordsSelected}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Record</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Primary Tenant</th>
                    {TENANTS.map(tenant => (
                      <th key={tenant.value} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        {tenant.label}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginatedRecords.map(record => {
                    const visibleTenants = editingRecords[record.id]?.visible_to_tenants || record.visible_to_tenants || [];
                    const hasChanges = !!editingRecords[record.id];
                    const isSelected = selectedRecords.has(record.id);
                    
                    return (
                      <tr key={record.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-3 text-center">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleSelectRecord(record.id)}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm max-w-md truncate">
                          {getDisplayName(record)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                            {record.tenant_id || 'default'}
                          </span>
                        </td>
                        {TENANTS.map(tenant => (
                          <td key={tenant.value} className="px-4 py-3 text-center">
                            <Checkbox
                              checked={visibleTenants.includes(tenant.value)}
                              onCheckedChange={() => handleTenantToggle(record.id, tenant.value)}
                            />
                          </td>
                        ))}
                        <td className="px-4 py-3 text-sm">
                          {hasChanges && (
                            <Button size="sm" onClick={() => handleSaveSingle(record)}>
                              <Save className="w-3 h-3 mr-1" /> Save
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}